/**
 * Item → sellable bundle, from carecentre-documents/public/subscription-item-mapping.html.
 *
 * People Management Platform is the union of these four and is not listed as a
 * separate upsell — hitting FEED should name CareCulture, not the platform.
 *
 * An item that ships on more than one bundle (EMPLOYEES, DIRECTORY, POLICIES,
 * EMPLOYEEUSER, ROSTER-ROLES) returns every owning product so the card can say
 * "CareRota or CareHR" rather than picking a winner.
 */
export const SUBSCRIPTION_BUNDLES = [
  {
    code: "carerota",
    name: "CareRota",
    tagline: "Rota planning, budgets, time and attendance.",
    unitAmount: 6,
    highlights: [
      "Shift scheduling",
      "Leave",
      "Time and attendance",
      "Visitor book",
    ],
    items: [
      "ROSTER",
      "LEAVE",
      "LEAVE-ACCRUAL",
      "TIMESHEET",
      "EMPLOYEES",
      "ROSTER-ROLES",
      "BUDGET",
      "DISCREPANCY",
      "TIMESHEET-REPORT",
      "DIRECTORY",
      "EMPLOYEE-WAGE",
      "MAINTENANCE",
      "VISITORS",
    ],
  },
  {
    code: "carehr",
    name: "CareHR",
    tagline: "HR records, onboarding, and compliance.",
    unitAmount: 3,
    highlights: [
      "Staff records",
      "Onboarding",
      "Policies",
      "Compliance tracker",
    ],
    items: [
      "EMPLOYEES",
      "ROSTER-ROLES",
      "DIRECTORY",
      "POLICIES",
      "ONBOARDING",
      "ONBOARDING-TEMPLATE",
      "MAINTENANCE",
    ],
  },
  {
    code: "careculture",
    name: "CareCulture",
    tagline: "Reward and retention — feed, chat, and surveys.",
    unitAmount: 3,
    highlights: ["Feed", "Chat", "Surveys", "Staff directory"],
    items: [
      "SURVEY",
      "SURVEY-INVITE",
      "SURVEY-RESPONSES",
      "FEED",
      "CHAT",
      "DIRECTORY",
      "POLICIES",
      "EMPLOYEEUSER",
      "ROSTER-ROLES",
      "MAINTENANCE",
    ],
  },
  {
    code: "careplanning",
    name: "CarePlanning",
    tagline: "Resident records and resident settings.",
    unitAmount: 3,
    highlights: [
      "Residents",
      "Care plans and rounds",
      "Resident settings",
      "Staff job titles",
    ],
    items: [
      "RESIDENT",
      "RESIDENT-SETTINGS",
      "EMPLOYEEUSER",
      "ROSTER-ROLES",
      "MAINTENANCE",
    ],
  },
];

const ITEM_LABELS = {
  ROSTER: "Rota",
  LEAVE: "Leave",
  "LEAVE-ACCRUAL": "Leave accrual",
  TIMESHEET: "Time and attendance",
  "TIMESHEET-REPORT": "Timesheet reports",
  EMPLOYEES: "Employees",
  EMPLOYEEUSER: "Users",
  "ROSTER-ROLES": "Roles",
  BUDGET: "Budgets",
  DISCREPANCY: "Extra time",
  DIRECTORY: "Directory",
  "EMPLOYEE-WAGE": "Wages",
  POLICIES: "Policies",
  ONBOARDING: "Onboarding",
  "ONBOARDING-TEMPLATE": "Onboarding templates",
  SURVEY: "Surveys",
  "SURVEY-INVITE": "Survey invites",
  "SURVEY-RESPONSES": "Survey responses",
  FEED: "Feed",
  CHAT: "Chat",
  RESIDENT: "Residents",
  "RESIDENT-SETTINGS": "Resident settings",
  MAINTENANCE: "Maintenance",
  ENQUIRIES: "Enquiries",
  VISITORS: "Visitors",
};

export const humanizeItem = (item) => {
  const raw = String(item || "").trim();
  if (!raw) return "this module";
  const key = raw.toUpperCase();
  if (ITEM_LABELS[key]) return ITEM_LABELS[key];
  return raw
    .replace(/[-_]/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

export const labelForItem = (item) => humanizeItem(item);

const itemKey = (item) =>
  String(item || "")
    .trim()
    .toUpperCase();

export const bundlesForItems = (items = []) => {
  const wanted = new Set(
    (Array.isArray(items) ? items : [items]).map(itemKey).filter(Boolean),
  );
  if (wanted.size === 0) return [];
  return SUBSCRIPTION_BUNDLES.filter((bundle) =>
    bundle.items.some((name) => wanted.has(name)),
  );
};

/** Bundle product names for these items, or a humanized fallback when none match. */
export const displayNamesForItems = (items = []) => {
  const names = bundlesForItems(items).map((bundle) => bundle.name);
  if (names.length > 0) return names;
  const list = Array.isArray(items) ? items : [items];
  const fallback = [...new Set(list.map(humanizeItem).filter(Boolean))];
  return fallback.length > 0 ? fallback : ["this module"];
};

/** Cheapest owning bundle's unit amount (£ / bed / month), or null. */
export const minBundleUnitAmount = (items = []) => {
  const amounts = bundlesForItems(items)
    .map((bundle) => bundle.unitAmount)
    .filter((amount) => Number.isFinite(amount));
  return amounts.length ? Math.min(...amounts) : null;
};

export const subscribePromptModel = (items = []) => {
  const list = (Array.isArray(items) ? items : [items]).filter(Boolean);
  const bundles = bundlesForItems(list);
  return {
    bundles,
    names: displayNamesForItems(list),
    price: minBundleUnitAmount(list),
    opened: [...new Set(list.map(labelForItem))],
    tagline:
      bundles.length === 1
        ? bundles[0].tagline
        : bundles.length > 1
          ? bundles.map((bundle) => bundle.tagline).join(" ")
          : "Book a short demo — we’ll show how this fits your care home.",
  };
};
